import { ErrorBoundary } from 'react-error-boundary';
import { AppProvider } from '@/app';
import { ErrorBoundaryFallback } from '@/components/ErrorBoundaryFallback';
import { AppContainer } from '@/containers/AppContainer';
import { APP_LINKS } from '@/helpers/links';
import { sentryCreateBrowserRouter } from '@/sentry';

import AuthPage from './pages/AuthPage';
import EmailVerificationStatusPage from './pages/EmailVerificationStatusPage';
import DevPage from './pages/DevPage';
import DashboardPage from './pages/DashboardPage';
import PageNotFound from './pages/PageNotFound';
import FundsPage from './pages/FundsPage';
import FundsAddPage from './pages/FundsAddPage';
import FundsWithdrawPage from './pages/FundsWithdrawPage';
import ProfilePage from './pages/ProfilePage';
import FundsBankAccountsPage from './pages/FundsBankAccountsPage';
import ReportsPage from './pages/ReportsPage';
import CashMovementsPage from './pages/CashMovementsPage';
import TaxReportPage from './pages/TaxReportPage';
import LoanTermsPage from './pages/LoanTermsPage';
import LoanTermsGroupDetailPage from './pages/LoanTermsGroupDetailPage';
import InvestingPage from './pages/InvestingPage';
import FounderDetailPage from './pages/FounderDetailPage';
import TermsOfUsePage from './pages/TermsOfUsePage';
import RoundHistoryPage from './pages/RoundHistoryPage';
import UnsubscribePage from './pages/UnsubscribePage';
import HelpPage from './pages/HelpPage';
import LoansPage from './pages/LoansPage';
import LoansLoanPage from './pages/LoansLoanPage';
import LoansSchedulePage from './pages/LoansSchedulePage';
import AffiliatePage from './pages/AffiliatePage';
import BrandsRootPage from './pages/BrandsRootPage';
import BrandsPage from './pages/BrandsPage';
import FoundersPage from './pages/FoundersPage';
import BrandDetailPage from '@/pages/BrandDetailPage';
import ClientQuestionsPage from './pages/ClientQuestionsPage';
import ComplaintsPage from './pages/ComplaintsPage';
import ComplaintAddPage from './pages/ComplaintAddPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import AuthDirectPage from './pages/AuthDirectPage';
import LoyaltyProgramPage from './pages/LoyaltyProgramPage';
import QuizCompletePage from './pages/QuizCompletePage';
import InvestmentStreakPage from './pages/InvestmentStreakPage';
import InvestmentStreakActivityPage from './pages/InvestmentStreakActivityPage';
import PortfolioMovementsPage from '@/pages/PortfolioMovementsPage';
import CalculatorPage from '@/pages/CalculatorPage';
import ComplianceFormPage from '@/pages/ComplianceFormPage';

const Routes = [
  {
    element: (
      <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
        <AppProvider />
      </ErrorBoundary>
    ),
    children: [
      {
        path: APP_LINKS.auth,
        element: <AuthPage />,
      },
      {
        path: APP_LINKS.signIn,
        element: <AuthPage />,
      },
      {
        path: APP_LINKS.complianceForm(':caseId'),
        element: <ComplianceFormPage />,
      },
      {
        path: APP_LINKS.authDirect,
        element: <AuthDirectPage />,
      },
      {
        path: APP_LINKS.quizComplete(),
        element: <QuizCompletePage />,
      },
      {
        path: APP_LINKS.emailVerificationStatus(),
        element: <EmailVerificationStatusPage />,
      },
      {
        path: '/unsubscribe/:token',
        element: <UnsubscribePage />,
      },
      {
        path: '/legal/terms-of-use/',
        element: <TermsOfUsePage />,
      },
      {
        path: APP_LINKS.root,
        element: <AppContainer />,
        children: [
          {
            path: APP_LINKS.dashboard,
            element: <DashboardPage />,
          },
          {
            path: APP_LINKS.investing,
            element: <InvestingPage />,
          },
          {
            path: APP_LINKS.roundHistory,
            element: <RoundHistoryPage />,
          },
          {
            path: APP_LINKS.calculator,
            element: <CalculatorPage />,
          },
          {
            path: APP_LINKS.affiliate,
            element: <AffiliatePage />,
          },
          {
            path: APP_LINKS.investmentStreakActivity,
            element: <InvestmentStreakActivityPage />,
          },
          {
            path: APP_LINKS.investmentStreak,
            element: <InvestmentStreakPage />,
          },
          {
            path: APP_LINKS.profile,
            element: <ProfilePage />,
          },
          {
            path: APP_LINKS.founderDetail(':id'),
            element: <FounderDetailPage />,
          },
          {
            path: APP_LINKS.startupDetail(':slug'),
            element: <BrandDetailPage />,
          },
          {
            path: APP_LINKS.cash,
            element: <FundsPage />,
            children: [
              {
                path: APP_LINKS.addFunds,
                element: <FundsAddPage />,
              },
              {
                path: APP_LINKS.withdraw,
                element: <FundsWithdrawPage />,
              },
              {
                path: APP_LINKS.bankAccounts,
                element: <FundsBankAccountsPage />,
              },
            ],
          },
          {
            path: APP_LINKS.reports,
            element: <ReportsPage />,
            children: [
              {
                path: APP_LINKS.cash_movements,
                element: <CashMovementsPage />,
              },
              {
                path: APP_LINKS.portfolio_movements,
                element: <PortfolioMovementsPage />,
              },
              {
                path: APP_LINKS.taxReports,
                element: <TaxReportPage />,
              },
            ],
          },
          {
            path: APP_LINKS.brandsRoot,
            element: <BrandsRootPage />,
            children: [
              {
                path: APP_LINKS.startups,
                element: <BrandsPage />,
              },
              {
                path: APP_LINKS.founders,
                element: <FoundersPage />,
              },
            ],
          },
          {
            path: APP_LINKS.helpLoanTerms(':version?'),
            element: <LoanTermsPage />,
          },
          {
            path: APP_LINKS.helpLoanTermsGroup(),
            element: <LoanTermsGroupDetailPage />,
          },
          {
            path: '/help/:slugLevelOne?/:slugLevelTwo?/:slugLevelThree?',
            element: <HelpPage />,
          },
          {
            path: APP_LINKS.help,
            element: <HelpPage />,
          },
          {
            path: APP_LINKS.loans,
            element: <LoansPage />,
            children: [
              {
                path: APP_LINKS.loansList,
                element: <LoansLoanPage />,
              },
              {
                path: APP_LINKS.loansSchedule,
                element: <LoansSchedulePage />,
              },
            ],
          },
          {
            path: APP_LINKS.clientQuestions,
            element: <ClientQuestionsPage />,
          },
          {
            path: APP_LINKS.complaints,
            element: <ComplaintsPage />,
          },
          {
            path: APP_LINKS.complaintCreate,
            element: <ComplaintAddPage />,
          },
          {
            path: '/dev',
            element: <DevPage />,
          },
          {
            path: APP_LINKS.blog,
            element: <BlogPage />,
          },
          {
            path: APP_LINKS.blogDetail(),
            element: <BlogDetailPage />,
          },
          {
            path: APP_LINKS.loyaltyProgram,
            element: <LoyaltyProgramPage />,
          },
        ],
      },
      {
        path: '*',
        element: <PageNotFound />,
      },
    ],
  },
];

/** @type {import('react-router-dom').RouterProviderProps['router']} */
export const AppRoutes = sentryCreateBrowserRouter(Routes);
